// personal-information.component.ts
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Observable } from "rxjs";
import { map, startWith } from "rxjs/operators";

import { MiPerfilService } from "../../../services/mi-perfil/mi-perfil.service";
import { AdicionalesService } from "src/app/services/catalogos/adicionales.service";
import { CatalogParamsService } from "@modules/shared/services/catalog-params.service";
import { CustomValidators } from "src/app/proveiders/CustomValidators";
import { CurrencyPipe } from '@angular/common';
import { growVertical } from "src/app/animations";

interface UnifiedProfile {
  bayport: any;
  personal: any;
  social: any;
  financial: any;
  banking: any;
}

interface CatalogItem {
  codigo: number;
  nombre: string;
}

@Component({
  selector: "personal-information",
  templateUrl: "./personal-information.component.html",
  styleUrls: ["../mi-perfil-page.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CurrencyPipe],
  animations: [ growVertical ]
})
export class PersonalInformationComponent implements OnInit {

  personalForm!: FormGroup;
  socialForm!: FormGroup;
  financialForm!: FormGroup;

  identificationTypes: CatalogItem[] = [];
  edoCivil: CatalogItem[] = [];
  scholarship: CatalogItem[] = [];
  depOptions: CatalogItem[] = [];
  cityOptions: CatalogItem[] = [];
  housing: CatalogItem[] = [];

  filteredDepartments$!: Observable<CatalogItem[]>;
  filteredCities$!: Observable<CatalogItem[]>;

  state = {
    tipoDocumento: "",
    numeroDocumento: "",
    fechaExpedicionDocumento: "",
    lugarExpedicionDocumento: "",
    primerNombre: "",
    segundoNombre: "",
    primerApellido: "",
    segundoApellido: "",
    fechaNacimiento: "",
    lugarNacimiento: "",
    eps: "",
    afp: "",
    arl: "",
  };

  genders = ["Femenino", "Masculino"];
  activeGenderIndex = 0;
  colorAdvertencia: "success" | "warning" | "info" = 'success';
  textoAdvertencia = 'La información fue guardada exitosamente';
  showAdvertencia = false;
  loadingAdvertencia = false;

  unifiedData!: UnifiedProfile;

  constructor(
    private fb: FormBuilder,
    private perfil: MiPerfilService,
    private addSrv: AdicionalesService,
    private catSrv: CatalogParamsService,
    private cd: ChangeDetectorRef,
    private currencyPipe: CurrencyPipe
  ) { }

  async ngOnInit(): Promise<void> {
    this.buildForms();
    await this.loadCatalogs();
    await this.loadAllData();
  }

  private buildForms(): void {
    this.personalForm = this.fb.group({
      tipoDocumento: [null],
      numeroDocumento: [null],
      primerNombre: [null],
      segundoNombre: [null],
      primerApellido: [null],
      segundoApellido: [null],
      fechaNacimiento: [null],
      lugarNacimiento: [null, Validators.required],
      fechaExpedicionDocumento: [null, Validators.required],
      lugarExpedicionDocumento: [null, Validators.required],
      departamento: [null, Validators.required],
      ciudad: [null, Validators.required],
      direccion: [null, [Validators.required, Validators.maxLength(50)]],
      estadoCivil: [null, Validators.required],
      estudiosRealizados: [null, Validators.required],
      tipoVivienda: [null, Validators.required],
      genero: [1],
      correo: [null, [Validators.required, Validators.email]],
      telefono: [
        null,
        [
          Validators.required,
          Validators.maxLength(10),
          CustomValidators.patternValidator(/^3/, { numberThree: true }),
          Validators.pattern(/^[0-9]+$/),
        ],
      ],
    });

    this.socialForm = this.fb.group({
      eps: [{ value: null, disabled: true }],
      afp: [{ value: null, disabled: true }],
      arl: [{ value: null, disabled: true }],
    });

    this.financialForm = this.fb.group({
      totalActivos: [null, [Validators.min(0)]],
      totalPasivos: [null, [Validators.min(0)]],
      gastos: [null, [Validators.required, Validators.min(0)]],
      ingresos: [null, [Validators.required, Validators.min(0)]],
      otrosIngresos: [null, [Validators.min(0)]],
      descripcionOtrosIngresos: [null, [Validators.maxLength(150)]],
      personasACargo: [0, [Validators.required, Validators.pattern(/^\d+$/)]],
    });
  }

  private async loadCatalogs(): Promise<void> {
    const [idRes, edoRes, schRes, depsRes, vivRes] = await Promise.all([
      this.catSrv
        .getIdentifacationType()
        .toPromise()
        .catch(() => ({ rows: [] })),
      this.addSrv
        .getEstadoCivil()
        .toPromise()
        .catch(() => ({ rows: [] })),
      this.catSrv
        .getScholarship()
        .toPromise()
        .catch(() => ({ rows: [] })),
      this.addSrv
        .getDepartamentos()
        .toPromise()
        .catch(() => ({ rows: [] })),
      this.addSrv
        .getTipoVivienda()
        .toPromise()
        .catch(() => []),
    ]);

    const idRows = Array.isArray(idRes) ? idRes : idRes.rows ?? [];
    const edoRows = Array.isArray(edoRes) ? edoRes : edoRes.rows ?? [];
    const schRows = Array.isArray(schRes) ? schRes : schRes.rows ?? [];
    const depRows = Array.isArray(depsRes) ? depsRes : depsRes.rows ?? [];
    const vivRows = Array.isArray(vivRes) ? vivRes : vivRes.rows ?? [];

    this.identificationTypes = idRows.map((r) => ({
      codigo: +r.id,
      nombre: r.nombre,
    }));
    this.edoCivil = edoRows.map((r) => ({
      codigo: +r.codigo,
      nombre: r.nombre,
    }));
    this.scholarship = schRows.map((r) => ({
      codigo: +r.codigoEstudio,
      nombre: r.nombreEstudio,
    }));
    this.depOptions = depRows.map((r) => ({
      codigo: +r.codDepto,
      nombre: r.nomDepto,
    }));
    this.housing = vivRows.map((r) => ({
      codigo: +r.codigo,
      nombre: r.descripcion,
    }));

    this.filteredDepartments$ = this.personalForm
      .get("departamento")!
      .valueChanges.pipe(
        startWith(""),
        map((val) => this.filterByName(this.depOptions, val))
      );

    this.cd.markForCheck();
  }

  private async loadAllData(): Promise<void> {
    // --- Call Unified Endpoint ---
    this.unifiedData = await this.perfil.getUnifiedProfile().toPromise();

    // Extract subobjects from unified data
    const { personal: p, social: s, financial: f } = this.unifiedData;

    this.state.tipoDocumento = p.tipoDocumento.toString();
    this.state.numeroDocumento = p.numeroDocumento;
    this.state.fechaExpedicionDocumento = p.fechaExpedicionDocumento;
    this.state.primerNombre = p.primerNombre;
    this.state.segundoNombre = p.segundoNombre;
    this.state.primerApellido = p.primerApellido;
    this.state.segundoApellido = p.segundoApellido;
    this.state.fechaNacimiento = p.fechaNacimiento;

    this.personalForm.patchValue({
      tipoDocumento: +p.tipoDocumento,
      numeroDocumento: p.numeroDocumento,
      primerNombre: p.primerNombre,
      segundoNombre: p.segundoNombre,
      primerApellido: p.primerApellido,
      segundoApellido: p.segundoApellido,
      fechaNacimiento: p.fechaNacimiento,
      fechaExpedicionDocumento: p.fechaExpedicionDocumento,
      departamento: +p.departamento,
      ciudad: +p.ciudad,
      direccion: p.direccion,
      estadoCivil: +p.estadoCivil,
      estudiosRealizados: +p.estudiosRealizados,
      tipoVivienda: +p.tipoVivienda,
      genero: +p.genero,
      correo: p.correo,
      telefono: p.celular,
      lugarExpedicionDocumento: +p.lugarExpedicionDocumento,
      lugarNacimiento: +p.lugarNacimiento,
      personasACargo: +p.personasACargo,
    });
    this.activeGenderIndex = p.genero === 2 ? 0 : 1;

    await this.onDeptoSelected(+p.departamento);
    this.state.lugarExpedicionDocumento =
      this.cityOptions.find((c) => c.codigo === +p.lugarExpedicionDocumento)
        ?.nombre || "";
    this.state.lugarNacimiento =
      this.cityOptions.find((c) => c.codigo === +p.lugarNacimiento)?.nombre ||
      "";

    this.socialForm.patchValue({
      eps: s.eps ?? "",
      afp: s.afp ?? "",
      arl: s.arl ?? "",
    });
    (["eps", "afp", "arl"] as const).forEach((key) => {
      const ctrl = this.socialForm.get(key)!;
      s[key] != null
        ? ctrl.enable({ onlySelf: true })
        : ctrl.disable({ onlySelf: true });
    });

    this.financialForm.patchValue({
      ...f,
      descripcionOtrosIngresos: f.descripcionOtrosIngresos ?? "",
    });

    this.cd.markForCheck();
  }

  async onDeptoSelected(cod: number): Promise<void> {
    const res = await this.addSrv
      .getCiudades(cod)
      .toPromise()
      .catch(() => ({ rows: [] }));
    const rows = res.rows ?? [];
    this.cityOptions = rows.map((r) => ({
      codigo: +r.codMunic,
      nombre: r.nomMunic,
    }));
    this.filteredCities$ = this.personalForm.get("ciudad")!.valueChanges.pipe(
      startWith(""),
      map((val) => this.filterByName(this.cityOptions, val))
    );
    this.cd.markForCheck();
  }

  onDeptoChange(): void {
    const cod = +this.personalForm.value.departamento;
    if (cod) this.onDeptoSelected(cod);
  }

  private filterByName(arr: CatalogItem[], term: any): CatalogItem[] {
    const v = (term ?? "").toString().toLowerCase();
    return arr.filter((i) => i.nombre.toLowerCase().includes(v));
  }

  get tipoDocumentoNombre(): string {
    const code = this.personalForm.value.tipoDocumento;
    return (
      this.identificationTypes.find((t) => t.codigo === code)?.nombre || ""
    );
  }

  private buildPersonalPayload(): any {
    const raw = this.personalForm.getRawValue();
    return {
      tipoDocumento: raw.tipoDocumento,
      numeroDocumento: raw.numeroDocumento,
      primerNombre: raw.primerNombre,
      segundoNombre: raw.segundoNombre,
      primerApellido: raw.primerApellido,
      segundoApellido: raw.segundoApellido,
      fechaNacimiento: raw.fechaNacimiento,
      fechaExpedicionDocumento: raw.fechaExpedicionDocumento,
      lugarExpedicionDocumento: String(raw.lugarExpedicionDocumento),
      lugarNacimiento: String(raw.lugarNacimiento),
      departamento: String(raw.departamento),
      ciudad: String(raw.ciudad),
      direccion: raw.direccion,
      estadoCivil: raw.estadoCivil,
      estudiosRealizados: raw.estudiosRealizados,
      tipoVivienda: raw.tipoVivienda,
      genero: raw.genero,
      correo: raw.correo,
      celular: raw.telefono,
      telefono: raw.telefono,
      personasACargo: this.financialForm.getRawValue().personasACargo,
      codigoAsesor: this.unifiedData.bayport.codigoAsesor
    };
  }

  async onSaveAll(): Promise<void> {
    this.loadingAdvertencia = true
    const personalPayload = this.buildPersonalPayload();
    const rawValues  = this.financialForm.getRawValue();
    
    const financialPayload = Object.keys(rawValues).reduce((acc, key) => {
      const value = rawValues[key];
      acc[key] = (value === null || value === undefined) ? 0 : value;
      return acc;
    }, {} as any);

    try {
      await this.perfil.updateInfoPersonal(personalPayload).toPromise();
      await this.perfil.updateInfoFinanciera(financialPayload).toPromise();
      this.textoAdvertencia = 'Perfil actualizado'
      this.exitoPeticion()
    } catch (err: any) {
      this.textoAdvertencia = 'Fallo al actualizar perfil'
      this.fallaPeticion()
    }
  }

  exitoPeticion() {
    this.colorAdvertencia = "success"
    this.textoAdvertencia = "La información fue guardada exitosamente"
    this.showAdvertencia = true
    this.loadingAdvertencia = false
    this.cd.detectChanges();
    setTimeout(() => {
      this.showAdvertencia = false;
      this.cd.detectChanges();
    }, 5000); // 5 segundos
  }

  fallaPeticion() {
    this.colorAdvertencia = "warning"
    this.textoAdvertencia = "Fallo la actualización"
    this.showAdvertencia = true
    this.loadingAdvertencia = false
    this.cd.detectChanges();
    setTimeout(() => {
      this.showAdvertencia = false;
      this.cd.detectChanges();
    }, 5000); // 5 segundos
  }
}
